import { useCallback, useMemo } from "react";

import { scopedProjectKey, scopeProjectRef } from "@cmmd-forge/client-runtime";

import { deriveLogicalProjectKeyMap } from "~/logicalProject";
import { resolveProjectGroupingMode } from "~/projectGrouping";
import { useProjectGroupingStore } from "~/projectGroupingStore";
import type { Project } from "~/types";

/**
 * Which logical project each physical checkout belongs to.
 *
 * The grouping mode is asked per REPOSITORY, not per project, so every checkout
 * of one repo necessarily gets the same answer — two disagreeing would split a
 * group in half and leave neither able to find the other.
 *
 * Note this goes through `deriveLogicalProjectKeyMap`, not
 * `deriveSidebarLogicalProjectKey`. The latter reads like the grouping entry
 * point and has no callers at all; wiring a mode into it would have been a
 * change that looks right and never runs.
 */
export function useSidebarProjectGrouping(orderedProjects: readonly Project[]) {
  const groupingOverrides = useProjectGroupingStore((state) => state.overrides);

  const physicalToLogicalKey = useMemo(
    () =>
      deriveLogicalProjectKeyMap(orderedProjects, (identity) =>
        resolveProjectGroupingMode(groupingOverrides, { repositoryIdentity: identity }),
      ),
    [orderedProjects, groupingOverrides],
  );

  const resolveLogicalKey = useCallback(
    (project: Project) =>
      physicalToLogicalKey.get(
        scopedProjectKey(scopeProjectRef(project.environmentId, project.id)),
      )!,
    [physicalToLogicalKey],
  );

  return { physicalToLogicalKey, resolveLogicalKey };
}
